/**
 * The order and purchase order pages' Needs attention chips (v1 completion I
 * §10, the mock-up's state 8): "2 need attention:" and a chip per alert naming
 * its rule and what it is on; a line's chips sit on its row. Each opens the
 * alert's sheet (`onOpen`). Nothing is drawn when nothing needs attention.
 */
import type { NeedsAttentionItem } from "@shared-core/data/needs-attention-types";
import { needAttentionCount } from "@shared-core/domain/needs-attention";
import { cn } from "../../primitives/utils";

export interface AlertChipsProps {
  alerts: NeedsAttentionItem[];
  onOpen: (alertId: string) => void;
  /** The page's subject: a chip on it says only its rule; one on anything else names what it is on. */
  subjectRef?: string;
  /** `strip`: the header's, with its count; `row`: a line row's, chips only. */
  variant?: "strip" | "row";
}

export function AlertChips({
  alerts,
  onOpen,
  subjectRef,
  variant = "strip",
}: AlertChipsProps) {
  if (alerts.length === 0) return null;
  const chips = alerts.map((alert) => {
    const isLate =
      alert.is_overdue ||
      alert.priority === "high" ||
      alert.priority === "urgent";
    const on =
      variant === "row" || alert.subject_ref === subjectRef
        ? null
        : chipSubject(alert, subjectRef);
    return (
      <button
        key={alert.id}
        type="button"
        onClick={() => onOpen(alert.id)}
        className={cn(
          "rounded-full border bg-card px-3 py-0.5 text-xs",
          isLate
            ? "border-destructive text-destructive"
            : "border-amber-600 text-amber-700 dark:border-amber-400 dark:text-amber-400",
          alert.state === "acknowledged" && "opacity-70",
        )}
      >
        {alert.headline}
        {on && ` · ${on}`}
      </button>
    );
  });
  if (variant === "row")
    return <span className="inline-flex flex-wrap gap-1">{chips}</span>;
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      aria-label="Needs attention"
    >
      <span className="text-xs text-muted-foreground">
        {needAttentionCount(alerts.length)}:
      </span>
      {chips}
    </div>
  );
}

/** What a chip is on, short: a line of this order by its suffix (".02"), anything else by its number. */
function chipSubject(
  alert: NeedsAttentionItem,
  subjectRef: string | undefined,
): string {
  if (
    alert.subject_kind === "order_line" &&
    subjectRef &&
    alert.subject_ref.startsWith(`${subjectRef}.`)
  ) {
    return alert.subject_ref.slice(subjectRef.length);
  }
  return alert.subject_ref;
}
